import type { ParsedUrlQuery } from 'querystring';

/**
 * 폴더 재생 URL 계약 — **쓰기와 읽기를 한 곳에서 소유한다.**
 *
 * 서버는 재생 커서를 들고 있지 않다(stateless). 그래서 "지금 폴더를 재생 중"이라는 사실은
 * 오직 URL 로만 전달되고, 그 URL 모양이 곧 계약이다. 예전에는 만드는 쪽만 여기 있고 읽는 쪽은
 * 소비처마다 `router.query.folderId` 처럼 생문자열로 흩어져 있었다 — 키 이름을 바꾸면 한쪽만
 * 고쳐지는 구조였다. 두 방향을 같이 둬서 쿼리 키 문자열이 이 파일 밖으로 새지 않게 한다.
 */
const FOLDER_ID_QUERY_KEY = 'folderId';
export const FOLDER_PLAY_MODE_QUERY_KEY = 'folderPlayMode';
const EXPERIENCE_PLAY_MODE = 'experience';

export const isFolderExperiencePlay = (value: string | string[] | undefined) =>
  value === EXPERIENCE_PLAY_MODE;

export interface FolderPlayContext {
  /** 재생 중인 폴더. 없으면 폴더 재생이 아니다. */
  folderId: string | undefined;
  /** 타인 폴더를 맛보기로 도는 중인지(튜토리얼 맵 경유). */
  isExperience: boolean;
}

/**
 * 지금 URL 이 폴더 재생인지 읽는다.
 *
 * `buildFolderPlayHref` 가 쓴 것을 그대로 되돌린다 — 둘은 짝이므로 한쪽만 고치지 말 것.
 */
export const readFolderPlayContext = (
  query: ParsedUrlQuery,
): FolderPlayContext => ({
  folderId: query[FOLDER_ID_QUERY_KEY]?.toString(),
  isExperience: isFolderExperiencePlay(query[FOLDER_PLAY_MODE_QUERY_KEY]),
});

export const buildFolderPlayKey = (
  entryMapHashId: string,
  originalEntryMapHashId: string | undefined,
  isExperience: boolean,
) =>
  isExperience && originalEntryMapHashId
    ? `${entryMapHashId}:${originalEntryMapHashId}`
    : entryMapHashId;

export const buildFolderPlayHref = ({
  folderId,
  entryMapHashId,
  tutorialMapHashId,
  isExperience,
}: {
  folderId: string;
  entryMapHashId: string;
  tutorialMapHashId?: string;
  isExperience: boolean;
}) => {
  const query = {
    [FOLDER_ID_QUERY_KEY]: folderId,
    ...(isExperience && {
      [FOLDER_PLAY_MODE_QUERY_KEY]: EXPERIENCE_PLAY_MODE,
    }),
  };

  if (isExperience && tutorialMapHashId) {
    return {
      pathname: `/play/${tutorialMapHashId}`,
      query: {
        ...query,
        __playCustomData: JSON.stringify({ entryMapHashId }),
      },
    };
  }

  return { pathname: `/play/${entryMapHashId}`, query };
};
